import {Injectable} from '@angular/core';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {map, shareReplay, switchMap, take} from 'rxjs/operators';
import {AngularFireFunctions} from '@angular/fire/compat/functions';
import {environment} from '../../../../../environments/environment';

export interface Customer {
  customerId: string;
  domain: string;
  designatedAdmin?: string;
  type?: string;
  altProjectCreds?: string; // For customers who are using non-standard Little SIS Sync Agent projects. Can be set to `prod1` through `prod5`.
}


@Injectable({
  providedIn: 'root'
})
export class CustomerService {

  public customerSubject: BehaviorSubject<Customer|null> = new BehaviorSubject(null);
  public appIsLoading = false;
  public customer: Customer;

  private customerId: string;
  private customer$: Observable<Customer> = new Observable<Customer>();

  constructor(private afs: AngularFirestore,
              private angularFireFunctions: AngularFireFunctions) {
  }

  getCustomerId(): string {
    return this.customerId;
  }

  setCustomerId(customerId) {
    this.customerId = customerId;
  }

  getCustomer(): Observable<Customer> {
    return this.customer$;
  }

  getCustomer$(): Observable<Customer> {
    return this.customerSubject;
  }

  loadCustomer(customerId: string): Observable<Customer> {
    this.appIsLoading = true;
    this.customerId = customerId;
    if (!this.customerId) {
      console.error('CustomerId not set');
      throw new Error('CustomerId not set');
    }
    try {
      const customerPath = `customers/${this.customerId}`;
      this.customer$ = this.afs.doc<Customer>(customerPath).valueChanges().pipe(shareReplay());
      this.customer$.subscribe(customer => {
        this.customer = customer;
        this.customerSubject.next(customer);
      });
    } catch (error) {
      console.error(' Error getting customer', error);
      this.appIsLoading = false;
    }
    return this.customer$;
  }

  verifyDWD(): Observable<{success: boolean; message?: string}> {

    const callable = this.angularFireFunctions.httpsCallable('customers-verifyDWD');
    return callable({customerId: this.getCustomerId()})
      .pipe(
        map(response => {
          if (response.errors) {
            throw response.errors;
          }
          return response;
        })
      );

  }

  getClientIdAndScopes(): { clientId: string; scopes: string} {
    if (!this.customer) {
      throw new Error('Customer not initialized!');
    }
    const scopes = environment.dwd.sync.scopes;
    if (this.customer.altProjectCreds) {
      return {clientId: environment.dwd.sync.client_names[this.customer.altProjectCreds], scopes};
    }
    return {clientId: environment.dwd.sync.client_names.default, scopes};
  }

}
